import { getUserFullName } from '../../../helpers'
import { convertMinsToHrsMins } from '../../../helpers/date'
import { formatDate } from '../../helpers/date'
import { getEnumKeyByEnumValue } from '../../helpers/enum'
import trans from '../../helpers/trans'
import getIncluded from '../../japi/getIncluded'
import getMultipleIncluded from '../../japi/getMultipleIncluded'
import { JapiDocument } from '../../japi/types/Document'
import { MealType } from '../../types'
import { DateFormat } from '../../types/dateFormatTypes'
import { RoleType } from '../../types/roleTypes'
import { Comment, CommentResource } from '../Comment/Comment'
import { transformToComment } from '../Comment/CommentTransformers'
import { Department, DepartmentIndexV3Resource, DepartmentResource } from '../Department/Department'
import { transformToDepartment } from '../Department/DepartmentTransformers'
import { DepartmentGroupResource } from '../DepartmentGroup/DepartmentGroup'
import { EmploymentTypeResource } from '../EmploymentType/EmploymentType'
import { getDummyEmploymentTypeResource } from '../EmploymentType/EmploymentTypeHelpers'
import { transformToEmploymentType } from '../EmploymentType/EmploymentTypeTransformers'
import { ExperienceResource } from '../Experience/Experience'
import { getDummyExperienceResource } from '../Experience/ExperienceHelpers'
import { transformToExperience } from '../Experience/ExperienceTransformers'
import { Feedback, FeedbackResource } from '../Feedback/Feedback'
import { transformToFeedback } from '../Feedback/FeedbackTransformers'
import { transformToPeriod } from '../Period/PeriodTransformers'
import { PersonResource } from '../Person/Person'
import { transformPersonV3Resource, transformToPerson } from '../Person/PersonTransformers'
import { Role, RoleResource } from '../Role/Role'
import { transformToRole } from '../Role/RoleTransformers'
import { ShiftResource, TrackShift } from '../Shift/Shift'
import { transformToTrackShift } from '../Shift/ShiftTransformers'
import { ShiftPlanningResource } from '../ShiftPlanning/ShiftPlanning'
import { transformToShiftPlanning } from '../ShiftPlanning/ShiftPlanningTransformers'
import {
    LegacyBasicUser,
    ShiftIndexUserResource,
    TrackOwner,
    UserResource,
} from '../User/User'
import {
    transformToBasicUser,
    transformToShiftPlanningUser,
    transformToTrackOwner,
    transformUserWithPersonV3Resource,
} from '../User/UserTransformers'
import {
    ExportTracksFormData,
    Track,
    TrackExportData,
    TrackIndexCommentV3Resource,
    TrackIndexFeedbackV3Resource,
    TrackIndexRoleV3Resource,
    TrackIndexShiftPlanningV3Resource,
    TrackIndexShiftV3Resource,
    TrackIndexUserV3Resource,
    TrackIndexV3Resource,
    TrackResource,
} from './Track'

export const transformToTrack = (
    resource: TrackResource,
    owner: TrackOwner,
    shift?: TrackShift,
    department?: Department,
    comments?: Comment[],
    feedback?: Feedback,
    assessor?: LegacyBasicUser,
): Track => {
    const mealKey = getEnumKeyByEnumValue(MealType, resource.attributes.meal || MealType.none)
    const billableStart = new Date(resource.attributes.billableStart)
    const billableEnd = resource.attributes.billableEnd ? new Date(resource.attributes.billableEnd) : new Date()

    const checkIn = new Date(resource.attributes.checkIn)
    const checkOut = resource.attributes.checkOut ? new Date(resource.attributes.checkOut) : new Date()

    if (! mealKey) {
        console.error(`[transformToTrack]: Unknown key found named "${resource.attributes.meal}".`)
    }

    return {
        accepted: resource.attributes.accepted,
        isOffline: resource.attributes.isOffline,
        billablePeriod: transformToPeriod(billableStart, billableEnd, !! resource.attributes.billableEnd),
        checkInOutPeriod: transformToPeriod(checkIn, checkOut, !! resource.attributes.checkOut),
        department: department || shift?.department,
        deviation: resource.attributes.deviation,
        deviationRemark: resource.attributes.deviationRemark || undefined,
        id: resource.id,
        meal: mealKey ? MealType[mealKey] : MealType.none,
        owner,
        pause: resource.attributes.pause || 0,
        shift,
        totalBillableTime: resource.attributes.totalBillableTime,
        unplannedRemark: resource.attributes.unplannedRemark || undefined,
        managerComments: comments,
        feedback,
        assessor: assessor ? getUserFullName(assessor) : undefined,
    }
}

export const getTrackFromApiData = (doc: JapiDocument, trackResource: TrackResource): Track => {
    const ownerResource = getIncluded(doc, trackResource, 'owner') as UserResource

    const ownerEmploymentTypeResource = getIncluded(doc, ownerResource, 'employmentType') as EmploymentTypeResource
    const ownerExperienceResource = getIncluded(doc, ownerResource, 'experience') as ExperienceResource
    const ownerPersonResource = getIncluded(doc, ownerResource, 'person') as PersonResource
    const ownerRoleResources = getMultipleIncluded(doc, ownerResource, 'roles') as RoleResource[]
    const ownerDepartmentResources = getMultipleIncluded(doc, ownerResource, 'departments') as DepartmentResource[]

    // TODO: Missing resources bug. BE should fix this in VALK-2240.
    if (! ownerEmploymentTypeResource) {
        console.error(`[getDashboardTracksApiCall] Missing employmentType resource in user with id ${ownerResource.id} (${ownerResource.attributes.firstname})`)
    }

    if (! ownerExperienceResource) {
        console.error(`[getDashboardTracksApiCall] Missing experience resource in user with id ${ownerResource.id} (${ownerResource.attributes.firstname})`)
    }

    if (! ownerRoleResources) {
        console.error(`[getDashboardTracksApiCall] Missing roles resource in user with id ${ownerResource.id} (${ownerResource.attributes.firstname})`)
    }

    const person = transformToPerson(ownerPersonResource)
    const employmentType = transformToEmploymentType(ownerEmploymentTypeResource || getDummyEmploymentTypeResource(`${ownerResource.id}-employment-type`))
    const experience = transformToExperience(ownerExperienceResource || getDummyExperienceResource(`${ownerResource.id}-experience`))
    const roles = (ownerRoleResources || []).map(transformToRole)
    const departments = ownerDepartmentResources?.map(departmentResource => transformToDepartment(departmentResource)) ?? []

    const owner = transformToTrackOwner(
        ownerResource,
        person,
        employmentType,
        experience,
        roles,
        departments,
    )

    const trackDepartmentResource = getIncluded(doc, trackResource, 'department') as DepartmentResource
    const trackDepartmentGroupResource = getIncluded(doc, trackDepartmentResource, 'group') as DepartmentGroupResource
    const trackDepartment = trackDepartmentResource ? transformToDepartment(trackDepartmentResource, trackDepartmentGroupResource) : undefined
    const shiftResource = getIncluded(doc, trackResource, 'shift') as ShiftResource
    const shiftDepartmentResource = shiftResource ? getIncluded(doc, shiftResource, 'department') as DepartmentResource : undefined
    const shiftDepartmentGroupResource = shiftDepartmentResource ? getIncluded(doc, shiftDepartmentResource, 'group') as DepartmentGroupResource : undefined
    const shiftShiftPlanningResources = shiftResource ? getMultipleIncluded(doc, shiftResource, 'shiftPlannings') as ShiftPlanningResource[] : undefined

    const shiftDepartment = shiftDepartmentResource ? transformToDepartment(shiftDepartmentResource, shiftDepartmentGroupResource) : undefined
    const shiftShiftPlannings = shiftShiftPlanningResources ? shiftShiftPlanningResources.map(shiftPlanningResource => {
        const userResource = getIncluded(doc, shiftPlanningResource, 'user') as ShiftIndexUserResource
        const shiftPlanningEmploymentTypeResource = getIncluded(doc, userResource, 'employmentType') as EmploymentTypeResource
        const shiftPlanningExperienceResource = getIncluded(doc, userResource, 'experience') as ExperienceResource

        const shiftPlanningEmploymentType = transformToEmploymentType(shiftPlanningEmploymentTypeResource || getDummyEmploymentTypeResource(
            `${userResource.id}-employment-type`,
        ))
        const shiftPlanningExperience = transformToExperience(shiftPlanningExperienceResource)
        const user = transformToShiftPlanningUser(userResource, shiftPlanningEmploymentType, shiftPlanningExperience)

        return transformToShiftPlanning(shiftPlanningResource, user)
    }) : undefined
    const shift = (shiftResource && shiftDepartment && shiftShiftPlannings) ? transformToTrackShift(shiftResource, shiftDepartment, shiftShiftPlannings) : undefined

    const commentsResource = getMultipleIncluded(doc, trackResource, 'comments') as CommentResource[]
    const comments = commentsResource.map(commentResource => {
        const userResource = getIncluded(doc, commentResource, 'owner') as UserResource

        return transformToComment(commentResource, userResource)
    })

    const feedbackResource = getIncluded(doc, trackResource, 'feedback') as FeedbackResource

    const feedback = feedbackResource ? transformToFeedback(feedbackResource) : undefined

    const assessorResource = getIncluded(doc, trackResource, 'assessor') as UserResource

    const assessor = assessorResource ? transformToBasicUser(assessorResource) : undefined

    return transformToTrack(
        trackResource,
        owner,
        shift,
        trackDepartment,
        comments,
        feedback,
        assessor,
    )
}

const transformDepartmentIndexV3ResourceToDepartment = (
    resource: DepartmentIndexV3Resource,
): Department => ({
    id: resource.uuid,
    name: resource.name,
    departmentGroup: resource.groupName
        ? { id: resource.groupName, name: resource.groupName }
        : undefined,
})

const transformTrackIndexShiftPlanningV3ResourceToShiftPlanning = (
    resource: TrackIndexShiftPlanningV3Resource,
): TrackShift['shiftPlannings'][number] => ({
    id: resource.uuid,
    customStartTime: resource.hasCustomTime,
    period: transformToPeriod(new Date(resource.start), new Date(resource.end)),
    ...(resource.user && { user: { id: resource.user.uuid } }),
})

const transformTrackIndexShiftV3ResourceToTrackShift = (
    resource: TrackIndexShiftV3Resource,
): TrackShift => ({
    id: resource.uuid,
    period: transformToPeriod(new Date(resource.start), new Date(resource.end)),
    shiftPlannings: resource.plannings.map(transformTrackIndexShiftPlanningV3ResourceToShiftPlanning),
})

const transformTrackIndexRoleV3Resource = (resource: TrackIndexRoleV3Resource): Role => {
    const roleTypeKey = getEnumKeyByEnumValue(RoleType, resource.slug)

    if (! roleTypeKey) {
        console.error(`[transformTrackIndexRoleV3Resource]: Unknown slug found named "${resource.slug}".`)
    }

    return {
        id: resource.uuid,
        name: resource.slug,
        slug: roleTypeKey ? RoleType[roleTypeKey] : RoleType.employee,
        public: true,
        assignable: true,
        priority: resource.priority,
    }
}

const transformTrackIndexUserV3Resource = (
    resource: TrackIndexUserV3Resource,
): TrackOwner => ({
    id: resource.uuid,
    person: transformPersonV3Resource(resource.person),
    roles: resource.roles.map(transformTrackIndexRoleV3Resource),
    departments: resource.departments.map(transformDepartmentIndexV3ResourceToDepartment),
})

const transformTrackIndexFeedbackV3ResourceToFeedback = (
    resource: TrackIndexFeedbackV3Resource,
): Feedback => ({
    rating: resource.rating,
    remark: resource.remark || undefined,
})

const transformTrackIndexCommentV3ResourceToComment = (
    resource: TrackIndexCommentV3Resource,
): Comment => ({
    id: resource.uuid,
    body: resource.body,
    type: 'tracks',
    createdAt: new Date(resource.createdAt),
    owner: resource.user ? transformUserWithPersonV3Resource(resource.user) : undefined,
})

export const transformTrackIndexV3Resource = (resource: TrackIndexV3Resource): Track => {
    const mealKey = getEnumKeyByEnumValue(MealType, resource.meal || MealType.none)
    const billableStart = new Date(resource.billableStart || resource.checkIn)
    const billableEnd = resource.billableEnd ? new Date(resource.billableEnd) : billableStart
    const checkIn = new Date(resource.checkIn)
    const checkOut = resource.checkOut ? new Date(resource.checkOut) : checkIn
    const department = resource.department
        ? transformDepartmentIndexV3ResourceToDepartment(resource.department)
        : undefined

    if (! mealKey) {
        console.error(`[transformTrackIndexV3Resource]: Unknown key found named "${resource.meal}".`)
    }

    return {
        accepted: resource.isApproved,
        isOffline: resource.isOffline,
        billablePeriod: transformToPeriod(billableStart, billableEnd, resource.billableEnd !== null),
        checkInOutPeriod: transformToPeriod(checkIn, checkOut, resource.checkOut !== null),
        department,
        deviation: resource.deviationMinutes || 0,
        deviationRemark: resource.deviationRemark || undefined,
        id: resource.uuid,
        meal: mealKey ? MealType[mealKey] : MealType.none,
        owner: transformTrackIndexUserV3Resource(resource.user),
        pause: resource.pauseMinutes,
        shift: resource.shift ? transformTrackIndexShiftV3ResourceToTrackShift(resource.shift) : undefined,
        totalBillableTime: convertMinsToHrsMins(resource.billableMinutes || 0),
        unplannedRemark: resource.unplannedRemark || undefined,
        managerComments: resource.comments.map(transformTrackIndexCommentV3ResourceToComment),
        feedback: resource.feedback
            ? transformTrackIndexFeedbackV3ResourceToFeedback(resource.feedback)
            : undefined,
        assessor: resource.approvedBy
            ? getUserFullName(transformUserWithPersonV3Resource(resource.approvedBy))
            : undefined,
    }
}

const getTrackExportDataFileName = (name: string, extension: string, formData: ExportTracksFormData): string => {
    const startDate = formatDate(formData.startDate, DateFormat.date)
    const endDate = formatDate(formData.endDate, DateFormat.date)

    return `${name}-${startDate}-${trans('common.until')}-${endDate}.${extension}`
}

export const transformTrackExportResponseToTrackExportData = (
    uri: string,
    name: string,
    extension: string,
    formData: ExportTracksFormData,
): TrackExportData => {
    const fileName = getTrackExportDataFileName(name, extension, formData)

    return { uri, fileName }
}
